import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PageHeader, Section } from "../components/ui-kit";
import { useContact } from "../context/SiteSettingsContext";

const Block = ({ heading, children }) => (
  <section className="space-y-3">
    <h2 className="font-heading text-2xl font-semibold text-stone-900">{heading}</h2>
    <div className="space-y-3 text-lg text-stone-700 leading-relaxed">{children}</div>
  </section>
);

const FEATURES = [
  "A skip to content link for keyboard and screen reader users",
  "Clear, visible keyboard focus on every link, button and form field",
  "Strong colour contrast and large, readable text",
  "A logical heading order on every page",
  "Descriptive alt text on images",
  "An Easy Read page written in short, simple sentences",
];

export default function AccessibilityStatement() {
  const CONTACT = useContact();
  return (
    <>
      <PageHeader
        eyebrow="Accessibility"
        title="Accessibility Statement"
        intro="We want everyone to be able to use this website. We have designed it with accessibility in mind, following the principles of WCAG 2.2 AA."
        testid="accessibility-header"
      />
      <Section tone="white">
        <div className="max-w-3xl space-y-10">
          <Block heading="Our commitment">
            <p>
              We are committed to making this website accessible to as many people as possible,
              including people with cognitive, sensory, physical and learning disabilities.
            </p>
          </Block>

          <section className="space-y-3">
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              What we have done
            </h2>
            <ul className="space-y-3">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3 text-lg text-stone-700">
                  <CheckCircle2 className="h-6 w-6 text-orange-700 shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </section>

          <Block heading="Easy Read">
            <p>
              We provide an{" "}
              <Link to="/easy-read" className="font-semibold text-orange-700 underline" data-testid="accessibility-easy-read-link">
                Easy Read page
              </Link>{" "}
              with short sentences and visual placeholders to make our information easier to
              understand.
            </p>
          </Block>

          <Block heading="Tell us what you think">
            <p>
              We keep working to improve accessibility. If you find something hard to use, or you
              need information in another format, please let us know.
            </p>
            <p>Email: {CONTACT.email}</p>
            <p>Phone: {CONTACT.phone}</p>
          </Block>
        </div>
      </Section>
    </>
  );
}
